import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import useModalEscape from '../../hooks/useModalEscape';
import scrollbar from '../../styles/scrollbar';
import { useT } from '../../i18n';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

/**
 * Every mode the host can pick. Adding one — practice, say — is this entry
 * plus its preset in the server's modes.js; nothing else here is per-mode.
 */
// Keys, not words. Adding a mode is still one entry here plus its preset in
// the server's modes.js — and now two catalog lines per language.
const MODE_CARDS = [
  { id: 'easy', labelKey: 'modes.easy', hintKey: 'modes.easyHint' },
  { id: 'normal', labelKey: 'modes.normal', hintKey: 'modes.normalHint' },
  { id: 'hardcore', labelKey: 'modes.hardcore', hintKey: 'modes.hardcoreHint' },
  { id: 'practice', labelKey: 'modes.practice', hintKey: 'modes.practiceHint' },
  { id: 'custom', labelKey: 'modes.custom', hintKey: 'modes.customHint' },
];

const REBID_LABELS = {
  anyone: 'modes.rebid.anyone',
  everyoneExceptLeader: 'modes.rebid.everyoneExceptLeader',
  oneBidPerPlayer: 'modes.rebid.oneBidPerPlayer',
};

const TIE_LABELS = {
  firstBidder: 'modes.tie.firstBidder',
  lowerScore: 'modes.tie.lowerScore',
};

// Who may ask the solver, and who hears it. The three values the server's
// SOLUTION_ACCESS allows — see modes.js.
const ACCESS_LABELS = {
  off: 'modes.never',
  host: 'modes.onRequest',
  everyone: 'modes.everyone',
};

const ACCESS_LABELS_LONG = {
  off: 'modes.never',
  host: 'modes.onRequestLong',
  everyone: 'modes.everyoneLong',
};

// `min` defaults to 10 — a clock of less than that is not a clock. minMoves is
// the exception and carries its own: 0 is a legal floor there, and it is how
// the setting is switched off.
const NUMBER_FIELDS = [
  { key: 'biddingTime', labelKey: 'modes.field.biddingTime', max: 600 },
  { key: 'demoTime', labelKey: 'modes.field.demoTime', max: 600 },
  { key: 'reconnectTime', labelKey: 'modes.field.reconnectTime', max: 300 },
  { key: 'minMoves', labelKey: 'modes.field.minMoves', min: 0, max: 8 },
];

/**
 * The settings actually in force, spelled out. The client is only ever told
 * the resolved settings of the *current* mode — the presets themselves live
 * in the server's modes.js — so this readout belongs to the active card and
 * nowhere else. Printing guessed numbers on the other cards would be a second
 * copy of that truth, and the palette in missions.json is a standing lesson
 * in what a second copy costs.
 */
function specs(settings, t) {
  return [
    [t('modes.spec.bidWindow'), t('modes.seconds', { count: settings.biddingTime })],
    [t('modes.spec.demoTurn'), t('modes.seconds', { count: settings.demoTime })],
    [t('modes.spec.rebids'), t(REBID_LABELS[settings.rebidRule])],
    [t('modes.spec.ties'), t(TIE_LABELS[settings.tieBreak])],
    [t('modes.spec.bestSolution'),
      t(settings.revealOptimal ? 'modes.announced' : 'modes.hidden')],
    [t('modes.spec.showSolution'),
      t(ACCESS_LABELS[settings.solutionAccess] ?? 'modes.never')],
    // A floor of 0 is off, and says so as a fact about the board rather than
    // as a zero — "0 moves or more" is true of every mission ever drawn.
    [t('modes.spec.difficulty'), settings.minMoves > 0
      ? t('modes.floor', { count: settings.minMoves })
      : t('modes.anyMission')],
  ];
}

function lockReason(isHost, phase, t) {
  if (!isHost) return t('modes.lockedNotHost');
  if (phase !== 'LOBBY') return t('modes.lockedStarted');
  return null;
}

const useStyles = createUseStyles({
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(10,6,3,0.68)',
    // Above SettingsModal (20), alongside ShortcutsModal (30) — both are
    // opened from Settings and stack on top of it.
    zIndex: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
  },
  modal: {
    background: '#2a1d13',
    border: '1px solid #6b4622',
    borderRadius: '14px',
    padding: '20px 24px 24px',
    maxWidth: '560px',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    maxHeight: '84vh',
    overflowY: 'auto',
    ...scrollbar,
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.12)',
      'inset 0 -3px 0 rgba(0,0,0,0.4)',
      '0 20px 50px rgba(0,0,0,0.55)',
    ].join(','),
    '@media (max-width: 620px)': {
      padding: '16px 16px 20px',
    },
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  // One way out, and it says where it lands. A bare ✕ next to a Back plate
  // would be two controls doing the same job.
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    minHeight: '44px',
    padding: '0 14px 0 10px',
    background: '#3a2416',
    border: 'none',
    borderRadius: '9px',
    color: '#d9c3a2',
    cursor: 'pointer',
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    boxShadow: [
      'inset 0 0 0 1px #4a3020',
      'inset 0 1px 0 rgba(255,255,255,0.08)',
      'inset 0 -2px 0 rgba(0,0,0,0.35)',
    ].join(','),
    transition: 'background .12s ease, color .12s ease',
    '&:hover': { background: '#472c1a', color: '#f0dcc0' },
    '&:focus-visible': { outline: '2px solid #e0a325', outlineOffset: '2px' },
  },
  backChevron: { fontSize: '14px', lineHeight: 1 },
  title: {
    margin: 0,
    color: '#f5e6c8',
    fontSize: '19px',
    fontWeight: 700,
    letterSpacing: '0.01em',
  },
  titleWrap: { display: 'flex', flexDirection: 'column', gap: '2px', textAlign: 'right' },
  titleMode: {
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#e0a325',
  },
  lockNote: {
    margin: 0,
    padding: '11px 14px',
    background: '#241608',
    borderRadius: '9px',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
    fontFamily: MONO,
    fontSize: '11px',
    letterSpacing: '0.04em',
    color: '#a8875d',
  },
  cards: { display: 'flex', flexDirection: 'column', gap: '10px' },
  card: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '14px 16px',
    background: '#3a2416',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    color: '#cbb493',
    boxShadow: [
      'inset 0 0 0 1px #4a3020',
      'inset 0 1px 0 rgba(255,255,255,0.08)',
      'inset 0 -3px 0 rgba(0,0,0,0.35)',
    ].join(','),
    transition: 'background .12s ease, color .12s ease',
    '&:hover': { background: '#472c1a', color: '#f0dcc0' },
    '&:focus-visible': { outline: '2px solid #e0a325', outlineOffset: '2px' },
    '&:disabled': { cursor: 'default' },
    '&:disabled:hover': { background: '#3a2416', color: '#cbb493' },
  },
  cardActive: {
    background: '#5c3a1e',
    color: '#f5e6c8',
    boxShadow: [
      'inset 0 0 0 1.5px #e0a325',
      'inset 0 1px 0 rgba(255,255,255,0.10)',
      'inset 0 -3px 0 rgba(0,0,0,0.4)',
    ].join(','),
    '&:hover': { background: '#5c3a1e', color: '#f5e6c8' },
    '&:disabled:hover': { background: '#5c3a1e', color: '#f5e6c8' },
  },
  cardHead: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '12px',
  },
  cardName: { fontSize: '16px', fontWeight: 700, color: 'inherit' },
  // Says something true — this is the mode actually running — rather than
  // decorating the card.
  inForce: {
    fontFamily: MONO,
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#e0a325',
    whiteSpace: 'nowrap',
  },
  cardHint: {
    display: 'block',
    marginTop: '4px',
    fontSize: '12.5px',
    lineHeight: 1.5,
    color: '#a8875d',
  },
  // The signature of this panel: the rules of the running game, stamped into
  // a recessed plate instead of hidden in a 9px run-on line.
  specs: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '7px 16px',
    marginTop: '12px',
    padding: '12px 14px',
    background: '#241608',
    borderRadius: '8px',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
  },
  specLabel: {
    fontFamily: MONO,
    fontSize: '9.5px',
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: '#8a6e4c',
    whiteSpace: 'nowrap',
    alignSelf: 'center',
  },
  specValue: {
    fontFamily: MONO,
    fontSize: '12.5px',
    letterSpacing: '0.02em',
    color: '#f0dcc0',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    background: '#241608',
    borderRadius: '10px',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
  },
  formRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '14px',
    '@media (max-width: 460px)': {
      alignItems: 'stretch',
      flexDirection: 'column',
      gap: '6px',
    },
  },
  formLabel: {
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#a8875d',
  },
  input: {
    width: '96px',
    minHeight: '44px',
    boxSizing: 'border-box',
    padding: '0 12px',
    background: '#150d07',
    border: '1px solid #533620',
    borderRadius: '7px',
    color: '#f0dcc0',
    fontFamily: MONO,
    fontSize: '14px',
    textAlign: 'right',
    outline: 'none',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
    transition: 'border-color .15s ease',
    '&:focus': { borderColor: '#e0a325' },
    '@media (max-width: 460px)': { width: '100%', textAlign: 'left' },
  },
  select: {
    flex: 1,
    maxWidth: '270px',
    minHeight: '44px',
    boxSizing: 'border-box',
    padding: '0 10px',
    background: '#150d07',
    border: '1px solid #533620',
    borderRadius: '7px',
    color: '#f0dcc0',
    fontFamily: MONO,
    fontSize: '12.5px',
    outline: 'none',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
    transition: 'border-color .15s ease',
    '&:focus': { borderColor: '#e0a325' },
    '@media (max-width: 460px)': { maxWidth: 'none' },
  },
  applyBtn: {
    width: '100%',
    minHeight: '46px',
    background: '#d94f2b',
    border: '1px solid #120c07',
    borderRadius: '8px',
    color: '#fff3e6',
    cursor: 'pointer',
    fontFamily: MONO,
    fontSize: '11.5px',
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.25)',
      '0 3px 0 #7d2a12',
    ].join(','),
    transition: 'background .12s ease',
    '&:hover': { background: '#e35c36' },
    '&:focus-visible': { outline: '2px solid #e0a325', outlineOffset: '2px' },
    '&:active': {
      transform: 'translateY(3px)',
      boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.45)',
    },
  },
});

/**
 * Game mode picker, in a panel of its own. Reached only from SettingsModal,
 * which stays mounted underneath — closing this returns there.
 *
 * `onChoose(mode, overrides?)` emits to the server, which resolves and
 * broadcasts the authoritative settings; nothing here is trusted.
 */
function ModesModal({ open, onClose, mode = 'normal', settings, isHost, phase, onChoose }) {
  const classes = useStyles();
  const t = useT();
  const [customOpen, setCustomOpen] = useState(false);
  const [overrides, setOverrides] = useState({});

  useModalEscape(open, onClose);

  if (!open) return null;

  const locked = lockReason(isHost, phase, t);
  const editable = !locked;

  const pick = (id) => {
    if (!editable) return;
    if (id === 'custom') {
      setCustomOpen(prev => !prev);
      return;
    }
    setCustomOpen(false);
    onChoose(id);
  };

  const field = (key) => overrides[key] ?? settings?.[key];

  const set = (key, value) => setOverrides(prev => ({ ...prev, [key]: value }));

  return (
    <div className={classes.overlay} onClick={onClose}>
      <div
        className={classes.modal}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('modes.title')}
      >
        <div className={classes.head}>
          <button type="button" className={classes.backBtn} onClick={onClose}>
            <span className={classes.backChevron} aria-hidden="true">‹</span>
            {t('modes.backToSettings')}
          </button>
          <div className={classes.titleWrap}>
            <h2 className={classes.title}>{t('modes.title')}</h2>
            <span className={classes.titleMode}>{mode}</span>
          </div>
        </div>

        {locked && <p className={classes.lockNote}>{locked}</p>}

        <div className={classes.cards}>
          {MODE_CARDS.map((card) => {
            const active = mode === card.id;
            return (
              <React.Fragment key={card.id}>
                <button
                  type="button"
                  disabled={!editable}
                  onClick={() => pick(card.id)}
                  className={`${classes.card} ${active ? classes.cardActive : ''}`}
                >
                  <span className={classes.cardHead}>
                    <span className={classes.cardName}>{t(card.labelKey)}</span>
                    {active && <span className={classes.inForce}>{t('modes.inForce')}</span>}
                  </span>
                  <span className={classes.cardHint}>{t(card.hintKey)}</span>

                  {active && settings && (
                    <span className={classes.specs}>
                      {specs(settings, t).map(([label, value]) => (
                        <React.Fragment key={label}>
                          <span className={classes.specLabel}>{label}</span>
                          <span className={classes.specValue}>{value}</span>
                        </React.Fragment>
                      ))}
                    </span>
                  )}
                </button>

                {card.id === 'custom' && editable && customOpen && (
                  <div className={classes.form}>
                    {NUMBER_FIELDS.map(({ key, labelKey, min = 10, max }) => (
                      <div className={classes.formRow} key={key}>
                        <label className={classes.formLabel} htmlFor={`mode-${key}`}>{t(labelKey)}</label>
                        <input
                          id={`mode-${key}`}
                          className={classes.input}
                          type="number"
                          min={min}
                          max={max}
                          value={field(key) ?? ''}
                          onChange={e => set(key, Number(e.target.value))}
                        />
                      </div>
                    ))}

                    <div className={classes.formRow}>
                      <label className={classes.formLabel} htmlFor="mode-rebidRule">{t('modes.spec.rebids')}</label>
                      <select
                        id="mode-rebidRule"
                        className={classes.select}
                        value={field('rebidRule')}
                        onChange={e => set('rebidRule', e.target.value)}
                      >
                        {Object.entries(REBID_LABELS).map(([v, l]) => (
                          <option key={v} value={v}>{t(l)}</option>
                        ))}
                      </select>
                    </div>

                    <div className={classes.formRow}>
                      <label className={classes.formLabel} htmlFor="mode-tieBreak">{t('modes.spec.ties')}</label>
                      <select
                        id="mode-tieBreak"
                        className={classes.select}
                        value={field('tieBreak')}
                        onChange={e => set('tieBreak', e.target.value)}
                      >
                        {Object.entries(TIE_LABELS).map(([v, l]) => (
                          <option key={v} value={v}>{t(l)}</option>
                        ))}
                      </select>
                    </div>

                    <div className={classes.formRow}>
                      <label className={classes.formLabel} htmlFor="mode-revealOptimal">{t('modes.spec.bestSolution')}</label>
                      <select
                        id="mode-revealOptimal"
                        className={classes.select}
                        value={field('revealOptimal') ? 'yes' : 'no'}
                        onChange={e => set('revealOptimal', e.target.value === 'yes')}
                      >
                        <option value="no">{t('modes.hidden')}</option>
                        <option value="yes">{t('modes.announcedLong')}</option>
                      </select>
                    </div>

                    <div className={classes.formRow}>
                      <label className={classes.formLabel} htmlFor="mode-solutionAccess">{t('modes.spec.showSolution')}</label>
                      <select
                        id="mode-solutionAccess"
                        className={classes.select}
                        value={field('solutionAccess')}
                        onChange={e => set('solutionAccess', e.target.value)}
                      >
                        {Object.entries(ACCESS_LABELS_LONG).map(([v, l]) => (
                          <option key={v} value={v}>{t(l)}</option>
                        ))}
                      </select>
                    </div>

                    <button
                      type="button"
                      className={classes.applyBtn}
                      onClick={() => { setCustomOpen(false); onChoose('custom', overrides); }}
                    >
                      {t('modes.applyCustom')}
                    </button>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ModesModal;
